import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { HardHat, AlertCircle } from 'lucide-react';

/**
 * AuthCallback page.
 * Emergent Auth redirects here as: /auth/callback#session_id=XXX
 * We synchronously process the session_id, exchange it for our JWT, and route by role.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const desiredRole = localStorage.getItem('clab_oauth_role') || undefined;

    if (!sessionId) {
      setError(t('auth.signInFailed'));
      setTimeout(() => navigate('/auth'), 2000);
      return;
    }

    (async () => {
      try {
        const user = await loginWithGoogleSession(sessionId, desiredRole);
        localStorage.removeItem('clab_oauth_role');
        // Clear the hash so the token never sticks in history
        window.history.replaceState({}, document.title, '/auth/callback');
        if (user.role === 'contractor') navigate('/contractor/dashboard', { replace: true });
        else if (user.role === 'admin') navigate('/admin/dashboard', { replace: true });
        else navigate('/client/dashboard', { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || t('auth.signInFailed'));
        setTimeout(() => navigate('/auth'), 2500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-orange-600 rounded-sm mx-auto mb-6 flex items-center justify-center">
          <HardHat className="w-8 h-8 text-white" />
        </div>
        {error ? (
          <>
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-white text-sm" data-testid="auth-callback-error">
              {error}
            </p>
          </>
        ) : (
          <>
            <div className="animate-spin w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-300 text-sm" data-testid="auth-callback-loading">
              {t('auth.finishingSignIn')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
